import {Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {NavigationService} from './navigation.service';
import {AuthApiService, Tokens, UserContextOption} from 'patja-api';
import {map, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {User} from 'patja-api';
import {HttpErrorResponse} from '@angular/common/http';
import {LocaleService} from './locale.service';
import {AccountingOfficeService} from './accounting-office.service';
import {ContextStorageService} from './context-storage.service';

export const USER_TOKEN: string = 'patja_token';
export const CHALLENGE: string = 'patja-challenge';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private user: WritableSignal<User | null> = signal(null);
  private availableContexts: WritableSignal<UserContextOption[]> = signal([]);
  private sessionExpiredTimeout: number | null = null;
  private refreshInFlight$: Observable<Tokens> | null = null;
  private storedTokens: Tokens | null = null;

  constructor(
    private navigationService: NavigationService,
    private authApiService: AuthApiService,
    private localeService: LocaleService,
    private accountingOfficeService: AccountingOfficeService,
    private contextStorageService: ContextStorageService,
  ) {}

  initialiseCurrentUser(): Observable<unknown> {
    this.migrateLegacyBrowserTokens();
    return this.authApiService.refreshToken(this.getStoredToken()).pipe(
      tap(tokens => this.storeToken(tokens)),
      catchError(() => of(null)),
      switchMap(() => {
        if (!this.getStoredToken()) {
          return of(null);
        }
        return this.authApiService.getLoggedInUser().pipe(
          catchError(err => {
            this.logout();
            return of(err);
          }),
          tap((user: User | HttpErrorResponse) => {
            if (user instanceof User) {
              this.setUser(user);
            }
          }),
        );
      }),
    );
  }

  setUser(user: User | null): void {
    this.user.set(user);
    this.setSessionExpiredTimer();

    if (user) {
      this.localeService.setLocale(user.langKey);
    }
  }

  private setSessionExpiredTimer(): void {
    const expDate: Date | null = this.getTokenExpiryDate();

    if (this.sessionExpiredTimeout) {
      clearTimeout(this.sessionExpiredTimeout);
      this.sessionExpiredTimeout = null;
    }

    if (!expDate) {
      return;
    }

    const delayUntilRefresh = expDate.getTime() - Date.now() - 60_000;
    if (delayUntilRefresh > 0) {
      this.sessionExpiredTimeout = window.setTimeout(
        () => this.refreshOrExpire(),
        delayUntilRefresh,
      );
    } else if (expDate.getTime() > Date.now()) {
      this.sessionExpiredTimeout = window.setTimeout(
        () => this.refreshOrExpire(),
        Math.max(expDate.getTime() - Date.now(), 0),
      );
    }
  }

  refreshSession(): Observable<Tokens> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authApiService.refreshToken(this.getStoredToken()).pipe(
        tap(tokens => this.storeToken(tokens)),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
      );
    }
    return this.refreshInFlight$;
  }

  private refreshOrExpire(): void {
    this.refreshSession().subscribe({
      error: () => this.logout(true),
    });
  }

  storeToken(token: Tokens): void {
    sessionStorage.removeItem(CHALLENGE);
    localStorage.removeItem(CHALLENGE);
    localStorage.removeItem(USER_TOKEN);
    this.storedTokens = token;
    this.setSessionExpiredTimer();
  }

  storeChallenge(token: string): void {
    sessionStorage.setItem(CHALLENGE, token);
  }

  clearStoredToken(): void {
    this.storedTokens = null;
    localStorage.removeItem(USER_TOKEN);
    sessionStorage.removeItem(USER_TOKEN);
  }

  getStoredToken(): string | null {
    return this.storedTokens?.idToken ?? null;
  }

  getChallenge(): string | null {
    return sessionStorage.getItem(CHALLENGE);
  }

  getTokenExpiryDate(): Date | null {
    return this.storedTokens?.exp ? new Date(this.storedTokens.exp) : null;
  }

  private migrateLegacyBrowserTokens(): void {
    const raw = localStorage.getItem(USER_TOKEN);
    localStorage.removeItem(USER_TOKEN);
    localStorage.removeItem(CHALLENGE);
    sessionStorage.removeItem(USER_TOKEN);
    if (this.storedTokens || !raw) {
      return;
    }
    try {
      this.storedTokens = new Tokens(JSON.parse(raw));
    } catch {
      this.storedTokens = null;
    }
  }

  logout(isSessionExpired?: boolean): void {
    const token = this.getStoredToken();
    this.clearStoredToken();
    this.setUser(null);
    this.accountingOfficeService.clearAccountingOffice();
    this.contextStorageService.clearStoredContext();

    this.authApiService.logout(token).subscribe({error: () => undefined});

    if (isSessionExpired) {
      this.navigationService.goToSessionExpired();
    } else {
      this.navigationService.goToLogin();
    }
  }

  getUser(): Observable<Signal<User | null>> {
    if (this.user()) {
      return this.loadAccountingOfficeForCurrentUser().pipe(map(() => this.user.asReadonly()));
    }

    return this.authApiService.getLoggedInUser().pipe(
      tap((user: User) => this.setUser(user)),
      switchMap(() => this.loadAccountingOfficeForCurrentUser()),
      map(() => this.user.asReadonly()),
    );
  }

  private loadAccountingOfficeForCurrentUser(): Observable<unknown> {
    const currentUser = this.user();
    if (!currentUser || currentUser.isCustomer()) {
      return of(null);
    }
    return this.accountingOfficeService
      .getAccountingOfficeForUser(currentUser.activeAccountingOfficeId ?? null)
      .pipe(map(() => null));
  }

  getInitializedUser(): User | null {
    return this.user();
  }

  getAvailableContexts(): Signal<UserContextOption[]> {
    return this.availableContexts.asReadonly();
  }

  loadAvailableContexts(): Observable<UserContextOption[]> {
    return this.authApiService
      .getAvailableContexts()
      .pipe(tap(contexts => this.availableContexts.set(contexts)));
  }

  resolvePreferredContext(
    contexts: UserContextOption[],
    user: User | null,
  ): UserContextOption | null {
    if (!contexts.length) {
      return null;
    }

    const stored = this.contextStorageService.getStoredContext();
    if (stored) {
      const storedMatch = contexts.find(context =>
        this.contextStorageService.matchesContext(context, stored),
      );
      if (storedMatch) {
        return storedMatch;
      }
    }

    const jwtMatch = contexts.find(context => this.matchesJwtContext(context, user));
    if (jwtMatch) {
      return jwtMatch;
    }

    return contexts[0] ?? null;
  }

  matchesJwtContext(context: UserContextOption, user: User | null): boolean {
    if (!user?.activeRole) {
      return false;
    }
    return (
      context.role === user.activeRole &&
      (context.companyId ?? null) === (user.activeCompanyId ?? null) &&
      (context.accountingOfficeId ?? null) === (user.activeAccountingOfficeId ?? null)
    );
  }

  switchActiveContext(context: UserContextOption): Observable<User> {
    return this.authApiService
      .setActiveContext({
        activeRole: context.role,
        activeCompanyId: context.companyId ?? null,
        activeAccountingOfficeId: context.accountingOfficeId ?? null,
      })
      .pipe(
        tap(token => this.storeToken(token)),
        switchMap(() => this.authApiService.getLoggedInUser()),
        tap(user => {
          this.setUser(user);
          this.contextStorageService.storeContext(context);
        }),
        switchMap(user =>
          user.isCustomer()
            ? of(user)
            : this.accountingOfficeService
                .getAccountingOfficeForUser(user.activeAccountingOfficeId ?? null)
                .pipe(map(() => user)),
        ),
      );
  }
}
